/**
 * The microsite's Sheet (microsites/womens-conference/src/components/Sheet.tsx)
 * in the widget: a floating right panel on desktop, a bottom sheet on phones,
 * garden paper with the grain riding the panel. The microsite drove vaul
 * directly; here the platform's ResponsiveSheet does the same two postures and
 * already portals into the widget's body-level overlay host, which vaul's
 * portal would not find inside a shadow root.
 *
 * Two things the platform sheet does not do our way, so they live here:
 *
 * - `band`: the picture across the top of the sheet, edge to edge, with the
 *   heading set on it. Same move as the group finder's SheetHeader and the
 *   event details' EventSheetBand, in this page's paint.
 * - The scroll hint. The platform's fog only knows bg-card, bg-white and a
 *   dark fallback, so on petal it laid a near-black wash over the books. It
 *   is switched off and replaced with a glass pill wearing a corsage.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ResponsiveSheet, SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import { art } from '../lib/art';
import { Rose, Sprig } from './Botanical';

export function Sheet({
  open,
  onClose,
  title,
  band,
  children,
  size = 'default',
}: {
  open: boolean;
  onClose: () => void;
  /** Screen-reader name; the content carries the visible heading. */
  title: string;
  /** A <SheetBand>. Dark, so the close button turns to dark glass over it. */
  band?: React.ReactNode;
  children: React.ReactNode;
  size?: 'default' | 'wide';
}) {
  return (
    <ResponsiveSheet
      open={open}
      onClose={onClose}
      placement="right"
      maxWidth={size === 'wide' ? 'max-w-[680px]' : 'max-w-[500px]'}
      panelClassName="xp-wm-sheet wm-grain bg-wm-petal text-wm-sage-950 md:rounded-[var(--radius-wm-card)]"
      noPanelPadding
      hideCloseButton
      hideScrollIndicator
    >
      <SheetPage name="main" title={title}>
        <SheetBody band={band} onClose={onClose}>
          {children}
        </SheetBody>
      </SheetPage>
    </ResponsiveSheet>
  );
}

function SheetBody({
  band,
  onClose,
  children,
}: {
  band?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const bodyRef = useRef<HTMLDivElement>(null);
  return (
    <div ref={bodyRef} className="relative">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className={`absolute top-4 right-4 z-10 flex h-9 w-9 items-center justify-center rounded-full text-lg transition-transform hover:scale-105 active:scale-95 ${
          band ? 'wm-glass-dark text-wm-cream' : 'wm-glass text-wm-sage-700'
        }`}
      >
        ×
      </button>
      {band}
      {children}
      <ScrollPill bodyRef={bodyRef} />
    </div>
  );
}

/**
 * The top of a sheet, edge to edge.
 *
 * With `image`, the picture is the header: shown whole at its own shape, the
 * heading set into a sage scrim along its foot. Without one, the masthead's
 * watercolor ground, darkened the same amount the hero and the question card
 * darken it, so every sheet on the page opens on the colour the page opened on.
 *
 * `lead` is whatever stands beside the heading on a painted band: the host's
 * face, the prayer rose.
 *
 * max-md:rounded-t-[2rem]: the phone's bottom sheet rounds only its drag strip
 * and does not clip what scrolls under it, so the band rounds itself to match.
 *
 * -mt-2 cancels the 8px the platform's SheetContent puts above a page with
 * no header, which otherwise leaves a strip of paper over the picture.
 *
 * z-[6] lifts it over the panel's paper coat (wm-grain's ::after, z-5): a
 * photograph with paper fibres multiplied into it reads as a bad scan.
 */
export function SheetBand({
  kicker,
  title,
  subtitle,
  image,
  imageAlt = '',
  imageSize,
  media,
  lead,
}: {
  kicker: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  image?: string;
  imageAlt?: string;
  /** The picture's own pixels, so the band holds its height while it loads. */
  imageSize?: { width: number; height: number };
  /** Something live in the picture's place, like a map. The heading and its
   *  scrim let touches through to it. */
  media?: React.ReactNode;
  lead?: React.ReactNode;
}) {
  const heading = (
    <div className="min-w-0">
      <p className="wm-kicker text-wm-cream/85">{kicker}</p>
      <h3 className="wm-display text-wm-cream mt-1 text-3xl sm:text-4xl">{title}</h3>
      {subtitle ? <p className="text-wm-sage-100 mt-1 text-sm font-medium">{subtitle}</p> : null}
    </div>
  );

  if (image || media) {
    return (
      <div className="bg-wm-sage-900 relative z-[6] -mt-2 overflow-hidden max-md:rounded-t-[2rem]">
        {media ?? (
          <img
            src={image}
            alt={imageAlt}
            width={imageSize?.width}
            height={imageSize?.height}
            className="block h-auto w-full"
            style={
              imageSize ? { aspectRatio: `${imageSize.width} / ${imageSize.height}` } : undefined
            }
          />
        )}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(to top, color-mix(in srgb, var(--color-wm-sage-950) 88%, transparent) 0%, color-mix(in srgb, var(--color-wm-sage-950) 60%, transparent) 30%, transparent 62%)',
          }}
        />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 px-6 pb-5 sm:px-7 sm:pb-6">
          {heading}
        </div>
      </div>
    );
  }

  return (
    <div className="relative z-[6] -mt-2 overflow-hidden px-6 pt-14 pb-6 max-md:rounded-t-[2rem] sm:px-7 sm:pt-16">
      <img
        src={art('/art/watercolor.png')}
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-cover"
      />
      <span aria-hidden className="bg-wm-sage-950/45 absolute inset-0" />
      <div className="relative flex items-center gap-4 pr-8">
        {lead ? <div className="shrink-0">{lead}</div> : null}
        {heading}
      </div>
    </div>
  );
}

/** The sheet's own scroll container, found from inside it. */
function scrollerOf(el: HTMLElement | null): HTMLElement | null {
  for (let node = el?.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
  }
  return null;
}

/**
 * "There is more below", as a glass pill with a corsage pinned to it: the rose
 * from the schedule, a leaf beside it, a bud at the other end. Tapping it
 * nudges most of a screen. Bows out 64px before the end, so it never sits on
 * the last line.
 *
 * Portalled into the panel (the scroll box's parent) and pinned to its foot.
 * Not sticky: the platform sheet wraps its pages in an overflow-hidden box for
 * the slide transition, and sticky pins to that, which never scrolls.
 */
function ScrollPill({ bodyRef }: { bodyRef: React.RefObject<HTMLDivElement | null> }) {
  const [show, setShow] = useState(false);
  const [panel, setPanel] = useState<HTMLElement | null>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const scroller = scrollerOf(bodyRef.current);
    scrollerRef.current = scroller;
    setPanel(scroller?.parentElement ?? null);
    if (!scroller) return;
    const check = () => {
      const remaining = scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop;
      setShow(scroller.scrollHeight > scroller.clientHeight + 32 && remaining > 64);
    };
    check();
    // The sheet slides in and its art lands after mount; both change the
    // heights without a scroll event.
    const late = window.setTimeout(check, 400);
    scroller.addEventListener('scroll', check, { passive: true });
    const ro = new ResizeObserver(check);
    ro.observe(scroller);
    if (bodyRef.current) ro.observe(bodyRef.current);
    return () => {
      window.clearTimeout(late);
      scroller.removeEventListener('scroll', check);
      ro.disconnect();
    };
  }, [bodyRef]);

  const nudge = () => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ top: el.clientHeight * 0.7, behavior: 'smooth' });
  };

  if (!panel) return null;
  return createPortal(
    <div
      className={`pointer-events-none absolute inset-x-0 bottom-5 z-10 flex justify-center transition-all duration-300 ${
        show ? 'translate-y-0 opacity-100' : 'invisible translate-y-2 opacity-0'
      }`}
    >
      <button
        type="button"
        onClick={nudge}
        tabIndex={show ? 0 : -1}
        aria-label="Scroll for more"
        // Near-opaque petal: over body copy, see-through glass let the words
        // underneath read through the label.
        className="wm-glass bg-wm-petal/90 text-wm-sage-700 hover:text-wm-sage-950 pointer-events-auto relative flex items-center gap-2 rounded-full py-2 pr-4 pl-6 transition-[color,transform] hover:scale-105 active:scale-95"
      >
        {/* The corsage. A petal halo round each stroke so it reads where it
            crosses the pill's rim and whatever text is behind it. */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 [&>svg]:[filter:drop-shadow(0_0_1px_var(--color-wm-petal))_drop-shadow(0_0_1px_var(--color-wm-petal))]"
        >
          <Rose className="text-wm-plum absolute -top-3 -left-2.5 h-7 w-7 -rotate-[24deg]" />
          <Sprig
            kind="leaf"
            className="text-wm-sage-500 absolute -top-2.5 left-3 h-5 w-5 rotate-[42deg]"
          />
          <Sprig
            kind="bud"
            className="text-wm-blush-deep absolute -right-2 -bottom-2.5 h-5 w-5 rotate-[140deg]"
          />
        </span>
        <span className="wm-display relative text-[17px] italic">More below</span>
        <span className="wm-hint-bob relative flex" aria-hidden>
          <svg
            viewBox="0 0 16 16"
            className="h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 6l4 4 4-4" />
          </svg>
        </span>
      </button>
    </div>,
    panel
  );
}
